import React from 'react';
import { useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import { store, RootStateOrAny } from 'src/store';
import { Divider, Button, Segment, Header } from 'semantic-ui-react';
import { uiSet } from '../../../pages/mainframe/mainframe.reducer';
import ImportGisModal from './GisImportModal';
import { getWithAuth, postWithAuth } from 'src/api';
import { runWithConfirmation } from 'src/utils/useful_functions';
import { getCanvas } from 'src/utils/mapUtils';
import { getLayersQueryKey } from './gisReducer';
import LayerConfigModal from './GisLayerConfigModal';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import messages from 'src/messages';

const openImportGisPopup = (showGisImportDialog) => {
  store.dispatch(uiSet({ showGisImportDialog: showGisImportDialog }));
};

const hideAllLayers = () => {
  const gis = getCanvas().gis;
  gis.hideAllGeomLayers();
};

function deleteGisLayer(projectId, filename, layerId, refreshFunc) {
  const data = { filename: filename };
  const url = layerId
    ? `project/${projectId}/gis_layers/${layerId}`
    : `project/${projectId}/gis_layers`;

  runWithConfirmation({
    header: layerId
      ? `Delete layer from ${filename}`
      : `Delete all layers from ${filename}`,
    onConfirm: () => {
      postWithAuth(url, data, 'DELETE').then(() => refreshFunc());
    },
  });
}

const showAllLayers = (projectId: string) => {
  // Fetch all layers at once
  getWithAuth(`project/${projectId}/gis_layers/geometry`).then((layers) => {
    const gis = getCanvas().gis;
    layers.forEach((layer) => {
      gis.showLayer(layer);
    });
  });
};

const showLayersForFile = (projectId: string, filename: string) => {
  // Fetch all layers for a particular file at once
  getWithAuth(
    `project/${projectId}/gis_layers/geometry?filename=${encodeURIComponent(
      filename
    )}`
  ).then((layers) => {
    const gis = getCanvas().gis;
    layers.forEach((layer) => {
      gis.showLayer(layer);
    });
  });
};

const showLayer = (
  projectId: string,
  layerId: string,
  fit: boolean = false
) => {
  // Fetch an individual layer
  getWithAuth(
    `project/${projectId}/gis_layers/geometry?layer_id=${layerId}`
  ).then((layers) => {
    const gis = getCanvas().gis;
    layers.forEach((layer) => {
      gis.showLayer(layer);
      if (fit) {
        gis.fitLayer(layerId);
      }
    });
  });
};

// Main Mode Panel
export default function GisModePanel() {
  const qc = useQueryClient();
  const { formatMessage } = useIntl();
  const projectId = useSelector(
    (state: RootStateOrAny) => state.mainFrame.projectId
  );
  const queryKey = getLayersQueryKey(projectId);

  const { data: layerData, isLoading } = useQuery({
    queryKey,
    queryFn: async () => {
      return await getWithAuth(`project/${projectId}/gis_layers`);
    },
    initialData: null,
    retry: false,
    enabled: true,
  });

  const refreshLayers = (layerIds?: string[]) => {
    const gis = getCanvas().gis;
    if (layerIds) {
      gis.hideLayers(layerIds);
      layerIds.forEach((layerId) => showLayer(projectId, layerId));
    } else {
      gis.hideAll();
    }
    qc.invalidateQueries({
      queryKey: getLayersQueryKey(projectId),
    });
  };

  const layers = !layerData || isLoading ? [] : layerData.layers;

  return (
    <>
      <Segment className="map-mode-panel">
        <Segment basic textAlign="center">
          <Header as="h2">{formatMessage(messages.gisMode)}</Header>

          <Divider hidden />
          <Button.Group vertical labeled icon>
            <Button
              color="blue"
              content="Import Layers"
              icon="plus"
              onClick={() => openImportGisPopup(true)}
              primary
            />
            <ShowAllHideAllButtons gisLayers={layers} />
          </Button.Group>
          <Divider hidden />
          <GeometryFilesPanel
            gisLayers={layers}
            projectId={projectId}
            refreshLayers={refreshLayers}
          ></GeometryFilesPanel>
        </Segment>
        <ImportGisModal
          onClose={() =>
            qc.invalidateQueries({
              queryKey: queryKey,
            })
          }
        />
      </Segment>
    </>
  );
}

// Global toggle
function ShowAllHideAllButtons({ gisLayers }) {
  const [projectId] = useSelector((state: RootStateOrAny) => [
    state.mainFrame.projectId,
  ]);

  // Calculate if all layers are visible
  const hasLayers = Object.keys(gisLayers).length > 0;

  return (
    hasLayers && (
      <>
        <Button
          icon={'eye slash'}
          content={'Hide All Layers'}
          onClick={() => {
            hideAllLayers();
          }}
        />
        <Button
          icon="eye"
          content="Show All Layers"
          onClick={() => {
            showAllLayers(projectId);
          }}
        />
      </>
    )
  );
}
// Global toggle ends

// Panel showing all of the KML/KMZ geometry files
function GeometryFilesPanel({ gisLayers, projectId, refreshLayers }) {
  const sortedFilenames = Object.keys(gisLayers).sort((f1, f2) =>
    f1.localeCompare(f2)
  );
  return (
    <div>
      {sortedFilenames.map((filename) => (
        <FileLayerGroup
          key={filename}
          filename={filename}
          projectId={projectId}
          layers={gisLayers[filename]}
          refreshLayers={refreshLayers}
        />
      ))}
    </div>
  );
}

function FileLayerGroup({
  filename,
  projectId,
  layers,
  refreshLayers,
}): React.JSX.Element {
  const sortedLayers = [...layers].sort((a, b) => {
    const order = { Point: 0, LineString: 1, Polygon: 2, Overlay: 3 };
    return (order[a.kind] ?? 99) - (order[b.kind] ?? 99);
  });

  const layerIds = sortedLayers.map((layer) => layer.id);

  const hideLayersForFile = () => {
    const gis = getCanvas().gis;
    gis.hideLayers(layerIds);
  };

  return (
    <div className="gis-layer">
      <div className="gis-layer-name" key={filename}>
        <h3>{filename}</h3>
        <Button.Group>
          <Button
            icon="eye"
            title={`Show all layers of ${filename}`}
            onClick={() => showLayersForFile(projectId, filename)}
          />
          <Button
            icon="eye slash"
            title={`Hide all layers of ${filename}`}
            onClick={() => hideLayersForFile()}
          />
          <Button
            icon="trash alternate"
            title="Delete all layers"
            onClick={() => {
              deleteGisLayer(projectId, filename, null, () =>
                refreshLayers(layerIds)
              );
            }}
          />
        </Button.Group>
      </div>

      <div className="gis-layer-group">
        {/* Sort the layer types in increasing order of complexity */}
        {sortedLayers.map((layer) => (
          <LayerRow
            key={`${filename}-${layer.kind}`}
            filename={filename}
            layer={layer}
            projectId={projectId}
            refreshLayers={refreshLayers}
          />
        ))}
      </div>
    </div>
  );
}

function LayerRow({ filename, layer, projectId, refreshLayers }) {
  const visibleLayers = useSelector(
    (state: RootStateOrAny) => state.geomLayers.visibleLayers
  );
  const isShown = visibleLayers.indexOf(layer.id) >= 0;
  const layerKind = layer.kind.replace('LineString', 'Line');

  return (
    <div className="gis-layer-row">
      <h4>{layerKind}</h4>

      <LayerPreview layer={layer} />

      <Button.Group>
        <LayerConfigModal
          layer={layer}
          projectId={projectId}
          onConfigChange={(config) => {
            refreshLayers([layer.id]);
          }}
        />
        <Button
          icon={isShown ? 'eye slash' : 'eye'}
          onClick={() => {
            if (isShown) {
              const gis = getCanvas().gis;
              gis.hideGeomLayer(layer.id);
            } else {
              showLayer(projectId, layer.id);
            }
          }}
          title={isShown ? 'Hide layer' : 'Show layer'}
        />
        <Button
          icon={'crosshairs'}
          onClick={() => {
            if (isShown) {
              getCanvas().gis.fitLayer(layer.id);
            } else {
              showLayer(projectId, layer.id, true);
            }
          }}
          title={'Focus layer on Map'}
        />
        <Button
          icon="trash alternate"
          title={`Delete the ${layerKind.toLowerCase()} geometry layer`}
          onClick={() => {
            deleteGisLayer(projectId, filename, layer.id, () =>
              refreshLayers([layer.id])
            );
          }}
        />
      </Button.Group>
    </div>
  );
}

const LayerPreview = ({ layer }) => {
  const style = layer.config || {};

  if (layer.kind === 'LineString') {
    return linePreview(style);
  } else if (layer.kind === 'Point') {
    return pointPreview(style);
  } else if (layer.kind === 'Polygon') {
    return polygonPreview(style);
  } else if (layer.kind === 'Overlay') {
    return overlayPreview(style);
  }

  return null;
};

function linePreview(style: any) {
  const strokeWidth = style.weight || 3;
  let dashArray = 'none';
  if (style.dashArray && style.dashArray !== 'solid') {
    const isDashed = style.dashArray === 'dashed';
    const dashSize = isDashed ? strokeWidth * 1.5 : strokeWidth * 0.5;
    const gapSize = isDashed ? strokeWidth * 0.5 : strokeWidth * 0.5;
    dashArray = `${dashSize} ${gapSize}`;
  }
  return (
    <svg width="40" height="10">
      <line
        x1="0"
        y1="3"
        x2="40"
        y2="3"
        stroke={style.color || '#000'}
        strokeWidth={strokeWidth}
        strokeDasharray={dashArray || '0'}
      />
    </svg>
  );
}

function polygonPreview(style: any) {
  return (
    <svg width="40" height="25">
      <polygon
        points="2,23 38,23 28,2 12,2"
        fill={style.color || '#000'}
        fillOpacity={style.opacity ?? 0.4}
        stroke={style.color || '#000'}
        strokeWidth="1.5"
      />
    </svg>
  );
}

function pointPreview(style: any) {
  const shape = style.markerShape || 'pin';
  const color = style.color || '#000';
  const opacity = style.opacity ?? 1;
  const radius = style.radius || 8;
  switch (shape) {
    case 'square':
      return (
        <svg width="40" height="20">
          <rect
            x={20 - radius}
            y={10 - radius}
            width={radius * 2}
            height={radius * 2}
            fill={color}
            fillOpacity={opacity}
          />
        </svg>
      );
    case 'triangle':
      return (
        <svg width="40" height="20">
          <polygon
            points={`${20},${10 - radius} ${20 - radius},${10 + radius} ${
              20 + radius
            },${10 + radius}`}
            fill={color}
            fillOpacity={opacity}
          />
        </svg>
      );
    case 'pin':
      const maxRadius = 12; // set in GisLayerConfigModal.tsx
      const scale = radius / maxRadius;
      return (
        <svg width="40" height="20">
          <path
            d="M 20.0,0 C 16.13,0 13,3.13 13,7 13,12.25 20.0,20 20.0,20 20.0,20 27,12.25 27,7 27,3.13 23.87,0 20.0,0 Z"
            fill={color}
            fillOpacity={opacity}
            transform={`translate(${(1 - scale) * 20}, ${
              (1 - scale) * 20
            }) scale(${scale})`}
          />
        </svg>
      );
    default:
      // circle
      return (
        <svg width="40" height="20">
          <circle
            cx="20"
            cy="10"
            r={radius}
            fill={color}
            fillOpacity={opacity}
          />
        </svg>
      );
  }
}

function overlayPreview(style: any) {
  return (
    <svg width="40" height="25">
      <g transform="translate(8,0.965)">
        <path
          d="M 11.99,18.54 4.62,12.81 3,14.07 l 9,7 9,-7 -1.63,-1.27 z M 12,16 19.36,10.27 21,9 12,2 3,9 4.63,10.27 Z"
          fill="#25478d"
          fillOpacity={style.opacity ?? 0.4}
        />
      </g>
    </svg>
  );
}
